/**
 * Sample partner brand themes — three demos that prove the white-label theming
 * machinery works end-to-end (per Design Brief §11 Phase 4 deliverable).
 *
 * Each theme is a flat object of token-path → override value. Per ADR-003 +
 * tokens v2 _open_questions_for_engineering.white_label_override_surface, only
 * `color.accent.*` paths and brand assets are overridable; foundation tokens
 * (typography, motion, spacing, radius, semantic mappings, callout structure)
 * are tenant-locked.
 *
 * Use via: resolveTokens(tokens, "navy-coral", SAMPLE_THEMES.consultancyA)
 *
 * The contrast-validation gate at theme publish runs before any tenant-published
 * theme can be persisted to the tenant_themes table. Each sample below has been
 * spot-checked for WCAG AA against bg-page (white) text-primary pairings.
 */

export const SAMPLE_THEMES = {
  // 1. Conservative HR consultancy — deep teal + warm taupe accent
  consultancyA: {
    "color.accent.navy":             "#173B3F",   // deep teal (replaces midnight navy)
    "color.accent.navy-dark":        "#0B2426",
    "color.accent.navy-soft":        "#D6E1E2",
    "color.accent.steel":            "#3F7B7F",
    "color.accent.steel-dark":       "#28575A",
    "color.accent.steel-soft":       "#DCEAEB",
    "color.accent.powder":           "#A6CACE",
    "color.accent.powder-soft":      "#E5EFF0",
    "color.accent.coral":            "#B97A4B",   // warm taupe instead of coral
    "color.accent.coral-dark":       "#8C5732",
    "color.accent.coral-soft":       "#F6EAD9",
    "color.accent.coral-very-soft":  "#FBF6EC",
    // Brand assets would normally be URL overrides; stub here.
  },

  // 2. Tech-forward HR partner — slate + electric cyan
  consultancyB: {
    "color.accent.navy":             "#1F2A44",
    "color.accent.navy-dark":        "#0F1424",
    "color.accent.navy-soft":        "#D9DEE7",
    "color.accent.steel":            "#3F4F73",
    "color.accent.steel-dark":       "#283655",
    "color.accent.steel-soft":       "#DDE2EC",
    "color.accent.powder":           "#A8B6CC",
    "color.accent.powder-soft":      "#E8ECF3",
    "color.accent.pale":             "#D5F0F4",
    "color.accent.pale-soft":        "#EBF8FA",
    "color.accent.coral":            "#0EA5BC",   // cyan as the warm/anchor instead of coral
    "color.accent.coral-dark":       "#0A7F92",
    "color.accent.coral-soft":       "#D9F1F5",
    "color.accent.coral-very-soft":  "#F0FAFC",
  },

  // 3. Wellbeing-forward HR consultancy — forest green + coral preserved
  consultancyC: {
    "color.accent.navy":             "#1F4A3A",   // forest
    "color.accent.navy-dark":        "#0E2A1F",
    "color.accent.navy-soft":        "#D6E2DD",
    "color.accent.steel":            "#3F6F5C",
    "color.accent.steel-dark":       "#264A3D",
    "color.accent.steel-soft":       "#DDE7E2",
    "color.accent.powder":           "#A8C4B7",
    "color.accent.powder-soft":      "#E5EEEA",
    // coral preserved — the wellbeing register keeps the warm anchor
  },
};

export const SAMPLE_THEME_LABELS = {
  consultancyA: "Conservative HR consultancy (deep teal + warm taupe)",
  consultancyB: "Tech-forward HR partner (slate + electric cyan)",
  consultancyC: "Wellbeing-forward consultancy (forest green + coral)",
};
